"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { apiPath } from "@/lib/apiPath";
import { Button } from "@/components/ui/Button";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import SafeHtml from "@/components/ui/SafeHtml";
import type { FeaturedProductsFields } from "@/types/acf";

type Props = { data: FeaturedProductsFields };
type AcfLink = { title?: string; url?: string; target?: string };

export default function FeaturedProductsSection({ data }: Props) {
  const products = (data?.products ?? []).slice(0, 4);
  const [links, setLinks] = useState<Record<number, AcfLink | null>>({});

  useEffect(() => {
    if (!products.length) return;
    let cancelled = false;

    (async () => {
      const entries = await Promise.all(
        products.map(async (p) => {
          const existing = (p.acf as { link?: AcfLink | null } | undefined)?.link;
          if (existing?.url) return [p.ID, existing] as const;

          try {
            const res = await fetch(apiPath(`/api/external-product-link?id=${p.ID}`));
            const json = await res.json();
            return [p.ID, json.link as AcfLink | null] as const;
          } catch {
            return [p.ID, null] as const;
          }
        })
      );

      if (!cancelled) setLinks(Object.fromEntries(entries));
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.map((p) => p.ID).join(",")]);

  if (!data || data.hide || !products.length) return null;

  const gridCols =
    products.length === 1
      ? "md:grid-cols-1"
      : products.length === 2 || products.length === 4
        ? "md:grid-cols-2"
        : "md:grid-cols-3";

  return (
    <>
      {data.section_title && (
        <h2 className="mb-6 text-center text-title-2 tablet:mb-8">
          <SafeHtml content={data.section_title} />
        </h2>
      )}

      <div className={`grid gap-6 max-md:full-width ${gridCols}`}>
        {products.map((p) => {
          const title = p.post_title ?? "";
          const link = links[p.ID];
          const imgSrc = p.featured_image ? toAbsoluteUrl(p.featured_image) : undefined;

          return (
            <article
              key={p.ID}
              className="
                group w-full p-6 bg-fuji-grey transition-colors duration-300
                [&:nth-child(1):hover]:bg-fuji-green/35
                [&:nth-child(2):hover]:bg-fuji-red/15
                [&:nth-child(3):hover]:bg-fuji-green/35
                [&:nth-child(4):hover]:bg-fuji-red/15
              "
            >
              <div className="flex h-full flex-col items-center text-center">
                <h3 className="mb-4 text-title-3 font-medium">
                  <SafeHtml content={title} />
                </h3>

                {imgSrc ? (
                  <div className="mb-6 transform-gpu transition-transform duration-300 group-hover:-translate-y-2">
                    <Image
                      src={imgSrc}
                      alt={title || "Product image"}
                      width={270}
                      height={270}
                      className="h-auto w-auto object-contain"
                    />
                  </div>
                ) : (
                  <div className="mb-6" />
                )}

                <div className="mt-auto flex justify-center">
                  <Button
                    as="a"
                    href={link?.url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    bgStyle="bg-transparent hover:bg-white border-fuji-black hover:border-white"
                  >
                    <SafeHtml content={link?.title || "Ver productos"} />
                  </Button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
}